import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Spinner,
  Alert, Badge, ProgressBar
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingService from '../../services/trainingService';
import { cropAndResizePhoto } from '../../utils/imageUtils';

const STEPS = ['Certificate Type', 'Select Course', 'Fill Form', 'Review & Submit'];

const NewTrainingCertificate = () => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);

  // Step control
  const [step, setStep] = useState(1);

  // Step 1 — Course selection
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseSearch, setCourseSearch] = useState('');

  // Step 2 — Cert type
  const [certType, setCertType] = useState('');

  // Step 3 — Dynamic form
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [headerData, setHeaderData] = useState({
    refNo: '',
    certNo: '',
    issueDate: '',
    validUntil: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [newSubmissionId, setNewSubmissionId] = useState(null);

  const [coursesError, setCoursesError] = useState('');

  // Load courses on mount
  useEffect(() => {
    (async () => {
      try {
        setCoursesLoading(true);
        setCoursesError('');
        const data = await trainingService.getCourses();
        if (data.success) {
          setCourses(data.courses);
        } else {
          setCoursesError(data.message || 'Failed to load courses');
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'Failed to load courses';
        setCoursesError(msg);
        console.error('Failed to load courses:', e);
      } finally {
        setCoursesLoading(false);
      }
    })();
  }, []);

  // Load fields when course + certType confirmed (entering step 3)
  useEffect(() => {
    if (step === 3 && selectedCourse) {
      (async () => {
        try {
          setFieldsLoading(true);
          const data = await trainingService.getCourseFields(selectedCourse.id);
          if (data.success) {
            setFields(data.fields);
            const init = {};
            data.fields.forEach(f => { init[f.fieldName] = ''; });
            setFormData(init);
          }
        } catch (e) {
          console.error('Failed to load fields:', e);
        } finally {
          setFieldsLoading(false);
        }
      })();
    }
  }, [step, selectedCourse]);

  const progressPercent = ((step - 1) / (STEPS.length - 1)) * 100;

  // ---- Step navigation ----

  const goNext = () => {
    if (step === 1 && !certType) return;
    if (step === 2 && !selectedCourse) return;
    if (step === 3) {
      const errors = validateStep3();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }
    setStep(s => s + 1);
  };

  const goBack = () => setStep(s => s - 1);

  // ---- Step 3 validation ----

  const validateStep3 = () => {
    const errors = {};
    fields.forEach(f => {
      if (f.isRequired && !formData[f.fieldName]?.toString().trim()) {
        errors[f.fieldName] = `${f.fieldLabel} is required`;
      }
    });
    return errors;
  };

  // ---- Photo handling ----

  const handlePhotoChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const blob = await cropAndResizePhoto(file, 105, 119);
      setPhotoFile(blob);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(blob);
    } catch (err) {
      alert('Could not process the image. Please try a different photo.');
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  }, []);

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // ---- Field change ----

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (formErrors[fieldName]) {
      setFormErrors(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
    }
  };

  // ---- Submit ----

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      const fd = new FormData();
      fd.append('courseId', selectedCourse.id);
      fd.append('certType', certType);
      if (headerData.refNo)    fd.append('refNo', headerData.refNo);
      if (headerData.certNo)   fd.append('certNo', headerData.certNo);
      if (headerData.issueDate)  fd.append('issueDate', headerData.issueDate);
      if (headerData.validUntil) fd.append('validUntil', headerData.validUntil);
      if (photoFile) fd.append('traineePhoto', photoFile, 'trainee_photo.jpg');

      Object.entries(formData).forEach(([key, val]) => {
        fd.append(`fieldData_${key}`, val);
      });

      const result = await trainingService.createSubmission(fd);
      if (result.success) {
        setSubmitSuccess(true);
        setNewSubmissionId(result.submissionId);
      } else {
        setSubmitError(result.message || 'Submission failed');
      }
    } catch (e) {
      setSubmitError(e.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render helpers ----

  const renderField = (field) => {
    const value = formData[field.fieldName] || '';
    const error = formErrors[field.fieldName];

    const commonProps = {
      id: field.fieldName,
      value,
      onChange: (e) => handleFieldChange(field.fieldName, e.target.value),
      isInvalid: !!error,
      placeholder: field.placeholder || '',
      required: field.isRequired,
      ...(field.fieldName === 'trnNm' ? { maxLength: 30 } : {}),
    };

    let input;
    switch (field.fieldType) {
      case 'textarea':
        input = <Form.Control as="textarea" rows={3} {...commonProps} />;
        break;
      case 'select': {
        const options = field.selectOptions
          ? field.selectOptions.split(',').map(o => o.trim()).filter(Boolean)
          : [];
        input = (
          <Form.Select {...commonProps}>
            <option value="">— Select —</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        );
        break;
      }
      case 'date':
        input = <Form.Control type="date" {...commonProps} />;
        break;
      case 'number':
        input = <Form.Control type="number" {...commonProps} />;
        break;
      default:
        input = <Form.Control type="text" {...commonProps} />;
    }

    return (
      <Form.Group key={field.id} className="mb-3">
        <Form.Label>
          {field.fieldLabel}
          {field.isRequired && <span className="text-danger ms-1">*</span>}
        </Form.Label>
        {input}
        {error && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{error}</Form.Control.Feedback>}
      </Form.Group>
    );
  };

  // Filter by selected cert type first, then by search text
  const filteredCourses = courses.filter(c =>
    c.certFamily === certType &&
    (
      c.courseName.toLowerCase().includes(courseSearch.toLowerCase()) ||
      (c.courseCode || '').toLowerCase().includes(courseSearch.toLowerCase())
    )
  );

  // Group fields by sectionName
  const fieldsBySectionMap = {};
  fields.forEach(f => {
    const sec = f.sectionName || 'General';
    if (!fieldsBySectionMap[sec]) fieldsBySectionMap[sec] = [];
    fieldsBySectionMap[sec].push(f);
  });
  const sections = Object.entries(fieldsBySectionMap);

  // ---- Success screen ----
  if (submitSuccess) {
    return (
      <>
        <TrainingNavbar />
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md={7}>
              <Card className="shadow text-center p-5">
                <div style={{ fontSize: '4rem' }} className="mb-3">
                  <i className="fas fa-check-circle text-success"></i>
                </div>
                <h3 className="text-success mb-3">Draft Saved!</h3>
                <p className="text-muted mb-4">
                  Your submission has been saved as a draft. Open it to review and then submit it for review when ready.
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <Button
                    variant="success"
                    onClick={() => navigate(`/training/submissions/${newSubmissionId}`)}
                  >
                    <i className="fas fa-eye me-2"></i>View & Submit for Review
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/training/coordinator')}>
                    Back to Dashboard
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <TrainingNavbar />
      <Container className="py-4" style={{ maxWidth: '900px' }}>
        {/* Header */}
        <div className="mb-4">
          <Button variant="link" className="ps-0 text-muted" onClick={() => navigate('/training/coordinator')}>
            <i className="fas fa-arrow-left me-1"></i> Back to Dashboard
          </Button>
          <h3 className="mt-1">New Training Certificate</h3>
        </div>

        {/* Progress */}
        <Card className="shadow-sm mb-4">
          <Card.Body className="pb-2">
            <Row className="mb-2">
              {STEPS.map((label, i) => (
                <Col key={i} className="text-center">
                  <span className={`badge rounded-pill px-3 py-2 ${
                    i + 1 === step ? 'bg-success' :
                    i + 1 < step  ? 'bg-secondary' : 'bg-light text-muted'
                  }`}>
                    {i + 1}. {label}
                  </span>
                </Col>
              ))}
            </Row>
            <ProgressBar now={progressPercent} variant="success" style={{ height: '6px' }} />
          </Card.Body>
        </Card>

        {/* ---- STEP 1: Certificate Type ---- */}
        {step === 1 && (
          <Card className="shadow-sm">
            <Card.Header><h5 className="mb-0">Step 1 — Certificate Type</h5></Card.Header>
            <Card.Body>
              <Row className="g-3 justify-content-center">
                {[
                  {
                    key: 'ADNOC',
                    label: 'ADNOC Training Certificate',
                    icon: 'fas fa-certificate',
                    color: 'primary',
                    desc: 'For ADNOC-certified training programs'
                  },
                  {
                    key: 'NON-ADNOC',
                    label: 'Non-ADNOC Training Certificate',
                    icon: 'fas fa-award',
                    color: 'info',
                    desc: 'For non-ADNOC training programs'
                  }
                ].map(opt => (
                  <Col md={5} key={opt.key}>
                    <Card
                      className={`text-center h-100 p-3 ${
                        certType === opt.key ? `border-${opt.color} border-2` : 'border'
                      }`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setCertType(opt.key);
                        setSelectedCourse(null); // reset course when type changes
                      }}
                    >
                      <Card.Body>
                        <i className={`${opt.icon} fa-3x text-${opt.color} mb-3`}></i>
                        <h5>{opt.label}</h5>
                        <p className="text-muted small">{opt.desc}</p>
                        {certType === opt.key && (
                          <Badge bg={opt.color}>
                            <i className="fas fa-check me-1"></i>Selected
                          </Badge>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
            <Card.Footer className="text-end">
              <Button variant="success" disabled={!certType} onClick={goNext}>
                Next <i className="fas fa-arrow-right ms-1"></i>
              </Button>
            </Card.Footer>
          </Card>
        )}

        {/* ---- STEP 2: Select Course (filtered by certType) ---- */}
        {step === 2 && (
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Step 2 — Select Course</h5>
              <small className="text-muted">
                Showing <Badge bg={certType === 'ADNOC' ? 'primary' : 'info'}>{certType}</Badge> courses only
              </small>
            </Card.Header>
            <Card.Body>
              <Form.Control
                type="search"
                placeholder="Search by course name…"
                className="mb-3"
                value={courseSearch}
                onChange={e => setCourseSearch(e.target.value)}
              />
              {coursesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="success" />
                </div>
              ) : coursesError ? (
                <Alert variant="danger">
                  <strong>Error loading courses:</strong> {coursesError}
                </Alert>
              ) : filteredCourses.length === 0 ? (
                <Alert variant="info">No {certType} courses found.</Alert>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <div className="d-flex flex-column gap-2">
                    {filteredCourses.map(course => (
                      <Card
                        key={course.id}
                        className={selectedCourse?.id === course.id ? 'border-success border-2' : 'border'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedCourse(course)}
                      >
                        <Card.Body className="py-2 px-3">
                          <div className="d-flex align-items-center gap-2">
                            {selectedCourse?.id === course.id
                              ? <i className="fas fa-check-circle text-success"></i>
                              : <i className="far fa-circle text-muted"></i>}
                            <span className="fw-semibold">{course.courseName}</span>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {selectedCourse && (
                <Alert variant="success" className="mt-3 mb-0 py-2">
                  <i className="fas fa-check me-2"></i>
                  Selected: <strong>{selectedCourse.courseName}</strong>
                  {selectedCourse.courseCode && ` (${selectedCourse.courseCode})`}
                </Alert>
              )}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={goBack}>
                <i className="fas fa-arrow-left me-1"></i> Back
              </Button>
              <Button variant="success" disabled={!selectedCourse} onClick={goNext}>
                Next <i className="fas fa-arrow-right ms-1"></i>
              </Button>
            </Card.Footer>
          </Card>
        )}

        {/* ---- STEP 3: Fill Form ---- */}
        {step === 3 && (
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Step 3 — Fill Form</h5>
              <small className="text-muted">
                {selectedCourse?.courseName} &bull;{' '}
                <Badge bg={certType === 'ADNOC' ? 'primary' : 'info'}>{certType}</Badge>
              </small>
            </Card.Header>
            <Card.Body>
              {fieldsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="success" />
                </div>
              ) : (
                <>
                  {/* ---- Trainee Photo ---- */}
                  <Card className="mb-4 border-success border-opacity-50">
                    <Card.Header className="bg-success bg-opacity-10">
                      <h6 className="mb-0">
                        <i className="fas fa-camera me-2 text-success"></i>
                        Trainee Photo
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col md={4} className="text-center">
                          {photoPreview ? (
                            <div className="position-relative d-inline-block">
                              <img
                                src={photoPreview}
                                alt="Trainee"
                                style={{
                                  width: '105px',
                                  height: '119px',
                                  objectFit: 'cover',
                                  border: '2px solid #198754',
                                  borderRadius: '4px'
                                }}
                              />
                              <button
                                onClick={removePhoto}
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  background: '#dc3545',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '22px',
                                  height: '22px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <div
                              style={{
                                width: '105px',
                                height: '119px',
                                border: '2px dashed #adb5bd',
                                borderRadius: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#adb5bd',
                                margin: '0 auto'
                              }}
                            >
                              <i className="fas fa-user fa-2x mb-2"></i>
                              <small>No photo</small>
                            </div>
                          )}
                        </Col>
                        <Col md={8}>
                          <Form.Label>Upload Trainee Photo</Form.Label>
                          <Form.Control
                            ref={photoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                          />
                          <Form.Text className="text-muted">
                            JPG or PNG only. Any size accepted.<br />
                            Photo will be automatically cropped and resized to <strong>105×119 px</strong> (passport style).
                            Preview updates instantly after selection.
                          </Form.Text>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* ---- Dynamic Sections ---- */}
                  {sections.map(([sectionName, sectionFields]) => (
                    <Card className="mb-4" key={sectionName}>
                      <Card.Header>
                        <h6 className="mb-0">{sectionName}</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          {sectionFields.map(field => (
                            <Col md={6} key={field.id}>
                              {renderField(field)}
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}

                  {fields.length === 0 && (
                    <Alert variant="info">
                      No additional fields defined for this course.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={goBack}>
                <i className="fas fa-arrow-left me-1"></i> Back
              </Button>
              <Button variant="success" onClick={goNext}>
                Review <i className="fas fa-arrow-right ms-1"></i>
              </Button>
            </Card.Footer>
          </Card>
        )}

        {/* ---- STEP 4: Review & Submit ---- */}
        {step === 4 && (
          <Card className="shadow-sm">
            <Card.Header><h5 className="mb-0">Step 4 — Review & Submit</h5></Card.Header>
            <Card.Body>
              {submitError && <Alert variant="danger">{submitError}</Alert>}

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Course:</strong> {selectedCourse?.courseName}
                </Col>
                <Col md={6}>
                  <strong>Certificate Type:</strong>{' '}
                  <Badge bg={certType === 'ADNOC' ? 'primary' : 'info'}>{certType}</Badge>
                </Col>
              </Row>

              {photoPreview && (
                <>
                  <hr />
                  <h6>Trainee Photo</h6>
                  <img
                    src={photoPreview}
                    alt="Trainee"
                    style={{ width: '105px', height: '119px', objectFit: 'cover', border: '1px solid #dee2e6' }}
                    className="mb-3"
                  />
                </>
              )}

              {fields.length > 0 && (
                <>
                  <hr />
                  <h6>Form Data</h6>
                  <Row>
                    {fields.map(f => (
                      <Col md={6} key={f.id} className="mb-2">
                        <small className="text-muted">{f.fieldLabel}</small>
                        <div>{formData[f.fieldName] || <span className="text-muted">—</span>}</div>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={goBack} disabled={submitting}>
                <i className="fas fa-arrow-left me-1"></i> Back
              </Button>
              <Button variant="success" onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <><Spinner size="sm" className="me-2" />Submitting…</>
                ) : (
                  <><i className="fas fa-paper-plane me-2"></i>Submit</>
                )}
              </Button>
            </Card.Footer>
          </Card>
        )}
      </Container>
    </>
  );
};

export default NewTrainingCertificate;
